import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '../components/AppLayout';
import { getDemoAuth } from '@/hooks/useAppAuth';
import { createNotification } from '@/hooks/useNotifications';
import { haversineDistance } from '@/hooks/distance';
import DaumPostcodeSearch from '@/components/base/DaumPostcodeSearch';
import AgreementSection from '@/components/feature/AgreementSection';

interface AddressValue {
  zonecode: string;
  roadAddress: string;
  jibunAddress: string;
  detailAddress: string;
}

interface PatientProfile {
  id: string;
  disease_name: string | null;
  patient_condition: string | null;
  toilet_assistance: string | null;
  height: string | null;
  weight: string | null;
  body_type: string | null;
  visit_type: string | null;
  preferred_caregiver_gender: string | null;
}

const ALL_REGIONS = ['서울', '경기', '인천', '부산', '대구', '광주', '대전', '울산', '세종', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주'];

/** 간병 신청 지역과 활동 지역이 일치하는 활성 간병인들에게 알림 전송 */
async function notifyNearbyCaregivers(careRegion: string, requesterUserId: string, homeAddress?: string) {
  try {
    // 1. 신청자 프로필 조회 (이름 + 위도/경도)
    const { data: requesterProfile } = await supabase
      .from('profiles')
      .select('name, latitude, longitude')
      .eq('id', requesterUserId)
      .maybeSingle();

    const requesterName = requesterProfile?.name || '환자';
    const requesterLat = requesterProfile?.latitude ? Number(requesterProfile.latitude) : null;
    const requesterLng = requesterProfile?.longitude ? Number(requesterProfile.longitude) : null;

    // 2. 모든 활성 간병인 조회 (지역 매칭은 JS에서 처리)
    const { data: allCaregivers, error: caregiverErr } = await supabase
      .from('caregiver_profiles')
      .select('user_id, available_regions')
      .eq('is_active', true);

    if (caregiverErr || !allCaregivers || allCaregivers.length === 0) {
      console.log('No active caregivers found');
      return;
    }

    // 3. 지역 매칭: 시/도 레벨 prefix 매칭 (예: "서울" → "서울 강남구", "서울 서초구" 등)
    //    홈어드레스가 있으면 시/군/구 레벨까지 추출해서 더 정확히 매칭
    let regionFilter = careRegion; // 기본: 시/도 레벨 (드롭다운 값)
    if (homeAddress) {
      // 주소에서 시/군/구 추출 시도
      const districtMatch = homeAddress.match(/(\S+(?:구|시|군))\s/);
      if (districtMatch) {
        const extracted = districtMatch[1];
        // 시/도 레벨 주소("서울특별시" 등)는 건너뛰고 구/군만 사용
        if (!/특별시|광역시|특별자치시|특별자치도/.test(extracted)) {
          regionFilter = `${careRegion} ${extracted}`;
        }
      }
    }

    // 4. 지역으로 간병인 필터링 (prefix 매칭)
    const nearbyCaregivers = allCaregivers.filter(
      (cg) =>
        Array.isArray(cg.available_regions) &&
        cg.available_regions.some((region: string) => region.startsWith(regionFilter))
    );

    // 시/군/구 레벨에서 매칭 안 됐으면 시/도 레벨로 재시도
    let finalCaregivers = nearbyCaregivers;
    if (finalCaregivers.length === 0 && regionFilter !== careRegion) {
      finalCaregivers = allCaregivers.filter(
        (cg) =>
          Array.isArray(cg.available_regions) &&
          cg.available_regions.some((region: string) => region.startsWith(careRegion))
      );
    }

    if (finalCaregivers.length === 0) {
      console.log('No nearby caregivers found for region:', careRegion);
      return;
    }

    // 5. 간병인들의 위치 정보 조회 (거리 계산용)
    const caregiverUserIds = finalCaregivers.map((c) => c.user_id);
    const { data: caregiverProfiles } = await supabase
      .from('profiles')
      .select('id, name, latitude, longitude')
      .in('id', caregiverUserIds);

    // 6. 각 간병인에게 알림 생성
    for (const caregiver of finalCaregivers) {
      // 자기 자신에게는 알림 보내지 않음 (혹시 간병인이 환자 role도 가지고 있을 경우)
      if (caregiver.user_id === requesterUserId) continue;

      const cp = caregiverProfiles?.find((p) => p.id === caregiver.user_id);
      let distanceText = '';

      // 거리 계산 (양쪽 모두 위도/경도가 있을 때만)
      if (requesterLat && requesterLng && cp?.latitude && cp?.longitude) {
        const km = haversineDistance(
          requesterLat,
          requesterLng,
          Number(cp.latitude),
          Number(cp.longitude),
        );
        distanceText = km < 1
          ? `약 ${Math.round(km * 1000)}m 거리`
          : `약 ${km.toFixed(1)}km 거리`;
      }

      await createNotification({
        userId: caregiver.user_id,
        type: 'match_request',
        title: `${careRegion} 지역 새 간병 요청`,
        body: `${requesterName}님의 간병 신청이 도착했습니다.${distanceText ? ` (${distanceText})` : ''} 지금 확인해보세요!`,
        relatedId: null,
      });
    }

    console.log(`Notifications sent to ${finalCaregivers.length} caregivers in ${careRegion}`);
  } catch (err) {
    console.error('Failed to notify nearby caregivers:', err);
    // 알림 실패는 신청 자체를 막지 않음
  }
}

export default function PatientCareRequestPage() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [patientProfile, setPatientProfile] = useState<PatientProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 간병 장소
  const [careLocationType, setCareLocationType] = useState<'집' | '병원'>('집');

  // 집 - 주소
  const [address, setAddress] = useState<AddressValue>({ zonecode: '', roadAddress: '', jibunAddress: '', detailAddress: '' });

  // 병원 - 병원명
  const [hospitalName, setHospitalName] = useState('');

  // 간병 조건
  const [careRegion, setCareRegion] = useState('');
  const [careStartDate, setCareStartDate] = useState('');
  const [careDurationDays, setCareDurationDays] = useState('');
  const [desiredDailyRate, setDesiredDailyRate] = useState('');
  const [visitType, setVisitType] = useState<'입주' | '출퇴근' | ''>('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [thirdPartyChecked, setThirdPartyChecked] = useState(false);

  useEffect(() => {
    async function init() {
      const demo = getDemoAuth();
      const uid = demo?.userId || null;
      if (!uid) { setLoading(false); return; }
      setUserId(uid);

      // 환자 프로필 확인
      const { data: pp } = await supabase
        .from('patient_profiles')
        .select('id, disease_name, patient_condition, toilet_assistance, height, weight, body_type, visit_type, preferred_caregiver_gender')
        .eq('user_id', uid)
        .maybeSingle();

      if (pp) {
        setPatientProfile(pp);
        // 프로필에 저장된 visit_type을 기본값으로
        if (pp.visit_type) {
          setVisitType(pp.visit_type as '입주' | '출퇴근');
        }
      }

      setLoading(false);
    }
    init();
  }, []);

  const isFormValid =
    careLocationType &&
    (careLocationType === '집' ? !!address.roadAddress : !!hospitalName) &&
    careRegion &&
    careStartDate &&
    careDurationDays &&
    parseInt(careDurationDays, 10) > 0 &&
    desiredDailyRate &&
    parseInt(desiredDailyRate, 10) > 0 &&
    privacyChecked &&
    thirdPartyChecked;

  async function handleSubmit() {
    if (!userId || !patientProfile) return;
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const { error: insertErr } = await supabase
        .from('patient_care_requests')
        .insert({
          user_id: userId,
          patient_profile_id: patientProfile.id,
          care_location_type: careLocationType,
          home_address: careLocationType === '집' && address.roadAddress ? JSON.stringify(address) : null,
          hospital_name: careLocationType === '병원' ? hospitalName : null,
          care_region: careRegion,
          care_start_date: careStartDate,
          care_duration_days: parseInt(careDurationDays, 10),
          desired_daily_rate: parseInt(desiredDailyRate, 10),
          visit_type: visitType || null,
          additional_notes: additionalNotes || null,
          status: 'pending',
        });

      if (insertErr) throw insertErr;

      // 근처 간병인들에게 알림 전송
      await notifyNearbyCaregivers(careRegion, userId, careLocationType === '집' ? address.roadAddress : undefined);

      setSuccess('간병매칭 신청이 완료되었습니다.');
      setTimeout(() => navigate('/app/matching', { replace: true }), 1200);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#E65100] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  if (!userId) {
    return (
      <AppLayout>
        <div className="min-h-screen flex flex-col items-center justify-center px-6">
          <p className="text-gray-500 mb-4">로그인이 필요합니다.</p>
          <button onClick={() => navigate('/app/login')} className="text-[#2E7D6B] font-bold cursor-pointer">로그인하기</button>
        </div>
      </AppLayout>
    );
  }

  if (!patientProfile) {
    return (
      <AppLayout>
        <div className="min-h-screen flex flex-col items-center justify-center px-6">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
            <i className="ri-error-warning-line text-3xl text-amber-500" />
          </div>
          <h2 className="text-lg font-extrabold text-gray-900 mb-2">환자 등록이 필요합니다</h2>
          <p className="text-sm text-gray-500 mb-6 text-center leading-relaxed">
            간병매칭을 신청하려면 먼저 환자 프로필을 등록해야 해요.
          </p>
          <button
            onClick={() => navigate('/app/register-patient')}
            className="py-3 px-6 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#2E7D6B' }}
          >
            환자 등록하러 가기
          </button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen px-5 pt-6 pb-24 bg-white">

        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/app')} className="w-9 h-9 rounded-xl border border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-50">
            <i className="ri-arrow-left-line text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-extrabold text-gray-900">간병매칭신청</h1>
            <p className="text-xs text-gray-400">원하는 조건으로 간병을 신청해보세요</p>
          </div>
        </div>

        {/* 등록된 환자 프로필 요약 */}
        <div className="p-4 rounded-xl bg-[#f0f9f6] border border-[#b8e6d8] mb-6">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-user-heart-line text-[#2E7D6B]" />
            <span className="text-xs font-bold text-[#2E7D6B]">등록된 환자 정보</span>
            <button
              onClick={() => navigate('/app/register-patient')}
              className="ml-auto text-[10px] text-[#2E7D6B] font-medium underline cursor-pointer hover:opacity-70"
            >
              수정
            </button>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-gray-700">
            {patientProfile.disease_name && (
              <div>
                <span className="text-gray-400">병명</span>
                <p className="font-medium">{patientProfile.disease_name}</p>
              </div>
            )}
            {patientProfile.patient_condition && (
              <div>
                <span className="text-gray-400">환자 상태</span>
                <p className="font-medium">{patientProfile.patient_condition}</p>
              </div>
            )}
            {patientProfile.toilet_assistance && (
              <div>
                <span className="text-gray-400">대소변 도움</span>
                <p className="font-medium">{patientProfile.toilet_assistance}</p>
              </div>
            )}
            {patientProfile.preferred_caregiver_gender && patientProfile.preferred_caregiver_gender !== '무관' && (
              <div>
                <span className="text-gray-400">선호 성별</span>
                <p className="font-medium">{patientProfile.preferred_caregiver_gender}</p>
              </div>
            )}
            {patientProfile.height && (
              <div>
                <span className="text-gray-400">키/체중</span>
                <p className="font-medium">{patientProfile.height}cm{patientProfile.weight ? ` / ${patientProfile.weight}kg` : ''}</p>
              </div>
            )}
            {patientProfile.body_type && (
              <div>
                <span className="text-gray-400">체질</span>
                <p className="font-medium">{patientProfile.body_type}</p>
              </div>
            )}
          </div>
        </div>

        {/* 간병 장소 선택 */}
        <div className="mb-6">
          <label className="block text-xs font-semibold text-gray-600 mb-2">
            간병 장소 <span className="text-red-400">*</span>
          </label>
          <div className="flex gap-3">
            {(['집', '병원'] as const).map((type) => (
              <button
                key={type}
                onClick={() => setCareLocationType(type)}
                className={`flex-1 py-3.5 rounded-xl border-2 text-sm font-bold cursor-pointer transition-all ${
                  careLocationType === type
                    ? 'border-[#E65100] text-[#E65100] bg-orange-50'
                    : 'border-gray-100 text-gray-500 bg-white hover:border-gray-200'
                }`}
              >
                <i className={`${type === '집' ? 'ri-home-4-line' : 'ri-hospital-line'} mr-1.5`} />
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* 집 선택 시 → 주소 입력 */}
        {careLocationType === '집' && (
          <div className="p-4 rounded-xl bg-orange-50 border border-orange-200 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-map-pin-line text-[#E65100]" />
              <span className="text-sm font-bold text-[#E65100]">간병 장소 주소</span>
            </div>
            <DaumPostcodeSearch
              label="주소"
              required
              placeholder="주소를 검색해주세요"
              value={address}
              onChange={setAddress}
            />
            {address.roadAddress && (
              <p className="mt-2 text-xs text-gray-500">
                <i className="ri-checkbox-circle-fill text-[#E65100] mr-1" />
                {address.roadAddress}{address.detailAddress ? `, ${address.detailAddress}` : ''}
              </p>
            )}
          </div>
        )}

        {/* 병원 선택 시 → 병원명 입력 */}
        {careLocationType === '병원' && (
          <div className="p-4 rounded-xl bg-orange-50 border border-orange-200 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-hospital-line text-[#E65100]" />
              <span className="text-sm font-bold text-[#E65100]">병원 정보</span>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                병원명 <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={hospitalName}
                onChange={(e) => setHospitalName(e.target.value)}
                placeholder="병원명을 입력하세요"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#E65100] bg-white"
              />
            </div>
          </div>
        )}

        {/* 간병 조건 */}
        <div className="space-y-4 mb-6">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-calendar-check-line text-[#E65100]" />
            <span className="text-sm font-bold text-[#E65100]">간병 조건</span>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              간병 필요 지역 <span className="text-red-400">*</span>
            </label>
            <select
              value={careRegion}
              onChange={(e) => setCareRegion(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#E65100] bg-white cursor-pointer"
            >
              <option value="">지역 선택</option>
              {ALL_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                간병 시작일 <span className="text-red-400">*</span>
              </label>
              <input
                type="date"
                value={careStartDate}
                onChange={(e) => setCareStartDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#E65100] bg-white cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                필요 기간 <span className="text-red-400">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={careDurationDays}
                  onChange={(e) => setCareDurationDays(e.target.value)}
                  placeholder="30"
                  min={1}
                  className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#E65100] bg-white"
                />
                <span className="text-xs text-gray-400 whitespace-nowrap">일</span>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              희망 간병비 (일당) <span className="text-red-400">*</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={desiredDailyRate}
                onChange={(e) => setDesiredDailyRate(e.target.value)}
                placeholder="예: 150000"
                min={0}
                step={10000}
                className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#E65100] bg-white"
              />
              <span className="text-xs text-gray-400 whitespace-nowrap">원/일</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">간병비는 당사자 간 직접 정산합니다.</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">간병 방식</label>
            <div className="flex gap-2">
              {(['입주', '출퇴근'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setVisitType(t)}
                  className={`flex-1 py-2.5 rounded-xl text-xs font-bold border-2 cursor-pointer transition-all ${
                    visitType === t
                      ? 'border-[#E65100] text-[#E65100] bg-orange-50'
                      : 'border-gray-100 text-gray-500 bg-white hover:border-gray-200'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              추가 요청사항 <span className="text-gray-400 font-normal">(선택)</span>
            </label>
            <textarea
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value.slice(0, 500))}
              placeholder="간병인에게 전달할 요청사항을 입력해주세요."
              rows={3}
              maxLength={500}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#E65100] bg-white resize-none"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{additionalNotes.length}/500</p>
          </div>
        </div>

        {/* 안내 */}
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 mb-6">
          <div className="flex items-start gap-2.5">
            <i className="ri-information-line text-amber-600 mt-0.5" />
            <div className="text-xs text-amber-800 leading-relaxed">
              <p className="font-semibold mb-1">간병비 정산 안내</p>
              <p>간병비는 환자/보호자와 간병인이 직접 정산합니다. 회사는 간병 완료 후 중개 수수료를 수취합니다.</p>
            </div>
          </div>
        </div>

        {/* 에러/성공 메시지 */}
        {error && <div className="mb-4 p-3 rounded-xl bg-red-50 text-xs text-red-500 text-center">{error}</div>}
        {success && <div className="mb-4 p-3 rounded-xl bg-green-50 text-xs text-green-600 text-center font-bold">{success}</div>}

        {/* 약관 동의 */}
        <div className="mb-6">
          <AgreementSection
            type="patient"
            privacyChecked={privacyChecked}
            thirdPartyChecked={thirdPartyChecked}
            onPrivacyChange={setPrivacyChecked}
            onThirdPartyChange={setThirdPartyChecked}
            disabled={saving}
          />
        </div>

        {/* 제출 버튼 */}
        <button
          onClick={handleSubmit}
          disabled={!isFormValid || saving}
          className="w-full py-3.5 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50"
          style={{ backgroundColor: '#E65100' }}
        >
          {saving ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin" />신청 중...
            </span>
          ) : (
            '간병매칭 신청하기'
          )}
        </button>

        <p className="text-[10px] text-gray-400 text-center mt-4 pb-4">
          같은 환자에 대해 장소, 기간, 조건을 달리하여 여러 건 신청할 수 있습니다
        </p>
      </div>
    </AppLayout>
  );
}